/**
 * `<SessionMark id size />`: the one component every surface draws a session's
 * mark with (RFC 0311 3c).
 *
 * An <img> over a data URI rather than injected markup: the SVG is a string we
 * built, but an image cannot run anything whatever it holds, and it needs no
 * ids, so two marks on one page never collide.
 */

import { type ReactNode, useMemo } from 'react'
import { sessionAvatar } from './sessionAvatar'

export function SessionMark({
  id,
  size,
  className,
  badge,
}: {
  id: string
  size: number
  className?: string
  /** Drawn over the lower right: the session's status dot (RFC 0311 3e). A
   *  slot rather than a status, because the one dot palette lives in the shell
   *  and a service must not import it. */
  badge?: ReactNode
}) {
  const src = useMemo(
    () =>
      `data:image/svg+xml;utf8,${encodeURIComponent(sessionAvatar(id, size))}`,
    [id, size],
  )
  const img = (
    <img
      src={src}
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={badge ? undefined : className}
      style={{ flex: 'none', display: 'block' }}
    />
  )
  if (!badge) return img
  return (
    <span
      className={`relative inline-block shrink-0 ${className ?? ''}`}
      style={{ width: size, height: size }}
    >
      {img}
      <span className="-right-0.5 -bottom-0.5 absolute flex rounded-full ring-2 ring-background">
        {badge}
      </span>
    </span>
  )
}
